import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import api from '../../api/api';

const SalesOrderHeader = ({
  orderDetails,
  user,
  setSuccess,
  setError,
  setOrderDetails,
}) => {
  const [editableAddress, setEditableAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [amountPaid, setAmountPaid] = useState(0);
  const [payMode, setPayMode] = useState('');
  const [refNo, setRefNo] = useState('');
  const [paymentModes, setPaymentModes] = useState([]);
  const [salesTypes, setSalesTypes] = useState([]);
  const [selectedSalesType, setSelectedSalesType] = useState({
    code: '',
    name: '',
  });
  const [orderDate, setOrderDate] = useState(''); 
  const [deliveryDate, setDeliveryDate] = useState(''); 

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (orderDetails) {
      setEditableAddress(orderDetails.Address || '');
      setAmountPaid(orderDetails.amountPaid ?? 0);
      setRefNo(orderDetails.Refno ?? '');
      setPayMode(orderDetails.Paymode ?? '');
      setPaymentModes(orderDetails.Table2 || []);
      setSalesTypes(orderDetails.Table3 || []);
      setOrderDate(formatDate(orderDetails.Podate));
      setDeliveryDate(formatDate(orderDetails.DeliveryDate)); // ✅ correctly bound

      if (orderDetails.SubTypeCode && orderDetails.SubTypeName) {
        setSelectedSalesType({
          code: orderDetails.SubTypeCode,
          name: orderDetails.SubTypeName,
        });
      }
    }
  }, [orderDetails]);

  const handleUpdateOrder = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        Poid: orderDetails?.Poid,
        Address: editableAddress,
        MobileNo: orderDetails?.MobileNo,
        User: user?.UsrName,
        amountPaid: amountPaid,
        Paymode: payMode,
        Refno: refNo,
        Podate: orderDate,
        DeliveryDate: deliveryDate, // ✅ correct key
        SubTypeCode: orderDetails?.SubTypeCode || selectedSalesType.code,
        SubTypeName: orderDetails?.SubTypeName || selectedSalesType.name,
      };

      console.log("🔹 Update Payload:", payload);

      await api.updateSalesOrderDetails(payload);

      if (setOrderDetails) {
        setOrderDetails((prev) => ({
          ...prev,
          Address: editableAddress,
          amountPaid,
          Paymode: payMode,
          Refno: refNo,
          Podate: orderDate,
          DeliveryDate: deliveryDate,
          SubTypeCode: payload.SubTypeCode,
          SubTypeName: payload.SubTypeName,
        }));
      }

      setSuccess('Order details updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error("❌ Update Failed:", err);
      setError('Failed to update order.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white shadow-lg rounded-xl overflow-hidden">
      <h2 className="text-lg sm:text-xl font-bold mb-4 text-center">
        Customer & Sales Order Info
      </h2>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Customer Name */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Customer Name
            </label>
            <input
              type="text"
              value={orderDetails?.Vname || ''}
              readOnly
              className="w-full p-2 border rounded-lg bg-gray-100 text-gray-700"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Address*
            </label>
            <textarea
              value={editableAddress}
              onChange={(e) => setEditableAddress(e.target.value)}
              rows="3"
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Amount Paid */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Amount Paid
            </label>
            <input
              type="number"
              value={amountPaid}
              onChange={(e) => setAmountPaid(Number(e.target.value))}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Reference No */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Reference No
            </label>
            <input
              type="text"
              value={refNo}
              onChange={(e) => setRefNo(e.target.value)}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Mobile */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Mobile
            </label>
            <input
              type="text"
              value={orderDetails?.MobileNo || ''}
              readOnly
              className="w-full p-2 border rounded-lg bg-gray-100 text-gray-700"
            />
          </div>

          {/* Balance */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Balance
            </label>
            <input
              type="text"
              value={(orderDetails?.Balance || 0).toLocaleString('en-IN', {
                style: 'currency',
                currency: 'INR',
              })}
              readOnly
              className="w-full p-2 border rounded-lg bg-gray-100 text-gray-700"
            />
          </div>

          {/* Delivery Date */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Delivery Date
            </label>
            <input
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Pay Mode */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">
              Pay Mode
            </label>
            <select
              value={payMode}
              onChange={(e) => setPayMode(e.target.value)}
              // disabled={!!orderDetails?.Paymode}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select</option>
              {paymentModes.map((mode, index) => (
                <option key={index} value={mode.Mode}>
                  {mode.Mode}
                </option>
              ))}
            </select>
          </div>

          {/* Sales Type */}
<div>
  <label className="block mb-2 text-sm font-bold text-gray-700">
    Sales Type
  </label>
  <select
    value={orderDetails.SubTypeCode || ''}
    onChange={(e) => {
      const selected = orderDetails.Table3.find(
        (st) => st.TypeStr === e.target.value
      );
      setOrderDetails({
        ...orderDetails,
        SubTypeCode: selected?.TypeStr || '',
        SubTypeName: selected?.TypeName || '',
      });
    }}
    className="w-full p-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    disabled={!!orderDetails?.Cpid} // ✅ Disable dropdown if Cpid exists
  >
    <option value="">Select Sales Type</option>
    {orderDetails.Table3?.map((st, idx) => (
      <option key={idx} value={st.TypeStr}>
        {st.TypeName}
      </option>
    ))}
  </select>
  <input
    type="hidden"
    name="SubTypeCode"
    value={orderDetails.SubTypeCode || ''}
  />
</div>

        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 mt-6">
        <Button
          type="button"
          onClick={handleUpdateOrder}
          disabled={loading}
          className="px-4 py-2"
        >
          {loading ? 'Updating...' : 'Update Sales Order Details'}
        </Button>
      </div>
    </div>
  );
};

export default SalesOrderHeader;





// import React, { useState, useEffect } from 'react';
// import Button from '../../components/ui/Button';
// import api from '../../api/api';

// const SalesOrderHeader = ({
//   orderDetails,
//   user,
//   setSuccess,
//   setError,
//   setOrderDetails,
// }) => {
//   const [editableAddress, setEditableAddress] = useState('');
//   const [loading, setLoading] = useState(false);
//   const [amountPaid, setAmountPaid] = useState(0);
//   const [payMode, setPayMode] = useState('');
//   const [refNo, setRefNo] = useState('');
//   const [paymentModes, setPaymentModes] = useState([]);
//   const [salesTypes, setSalesTypes] = useState([]);
//   const [selectedSalesType, setSelectedSalesType] = useState({
//     code: '',
//     name: '',
//   });
//   const [orderDate, setOrderDate] = useState('');

//   const formatDate = (dateString) => {
//     if (!dateString) return '';
//     const date = new Date(dateString);
//     return date.toISOString().split('T')[0];
//   };

//   useEffect(() => {
//     if (orderDetails) {
//       setEditableAddress(orderDetails.Address || '');
//       setAmountPaid(orderDetails.amountPaid ?? 0);
//       setRefNo(orderDetails.Refno ?? '');
//       setPayMode(orderDetails.Paymode ?? '');
//       setPaymentModes(orderDetails.Table2 || []);
//       setSalesTypes(orderDetails.Table3 || []);
//       setOrderDate(formatDate(orderDetails.Podate));

//       // map swapped fields correctly
//       if (orderDetails.SubTypeCode && orderDetails.SubTypeName) {
//         setSelectedSalesType({
//           code: orderDetails.SubTypeName, // actual TypeStr
//           name: orderDetails.SubTypeCode, // actual TypeName
//         });
//       }
//     }
//   }, [orderDetails]);

//   const handleUpdateOrder = async (e) => {
//   if (e) e.preventDefault();
//   setLoading(true);
//   setError('');
//   setSuccess('');

//   try {
//     const payload = {
//       Poid: orderDetails?.Poid,
//       Address: editableAddress,
//       MobileNo: orderDetails?.MobileNo,
//       User: user?.UsrName,
//       amountPaid: amountPaid,
//       Paymode: payMode,
//       Refno: refNo,
//       Deliverydate: orderDate, 
//       SubTypecode: orderDetails?.SubTypeCode || selectedSalesType.code,
//       Subtypename: orderDetails?.SubTypeName || selectedSalesType.name,
//     };

//     console.log("🔹 Update Payload:", payload);

//     const res = await api.updateSalesOrderDetails(payload);

//     if (setOrderDetails) {
//       setOrderDetails((prev) => ({
//         ...prev,
//         Address: editableAddress,
//         amountPaid,
//         Paymode: payMode,
//         Refno: refNo,
//         Podate: orderDate,
//         SubTypeCode: payload.SubTypecode,
//         SubTypeName: payload.Subtypename,
//       }));
//     }

//     setSuccess('Order details updated successfully!');
//     setTimeout(() => setSuccess(''), 3000);
//   } catch (err) {
//     console.error("❌ Update Failed:", err);
//     setError('Failed to update order.');
//   } finally {
//     setLoading(false);
//   }
// };

//   return (
//     <div className="w-full max-w-4xl mx-auto bg-white shadow-lg rounded-xl overflow-hidden">
//       <h2 className="text-lg sm:text-xl font-bold mb-4 text-center">
//         Customer & Sales Order Info
//       </h2>

//       <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
//         {/* Left Column */}
//         <div className="space-y-4">
//           {/* Customer Name */}
//           <div>
//             <label className="block mb-2 text-sm font-bold text-gray-700">
//               Customer Name
//             </label>
//             <input
//               type="text"
//               value={orderDetails?.Vname || ''}
//               readOnly
//               className="w-full p-2 border rounded-lg bg-gray-100 text-gray-700"
//             />
//           </div>

//           {/* Address */}
//           <div>
//             <label className="block mb-2 text-sm font-bold text-gray-700">
//               Address*
//             </label>
//             <textarea
//               value={editableAddress}
//               onChange={(e) => setEditableAddress(e.target.value)}
//               rows="3"
//               className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Amount Paid */}
//           <div>
//             <label className="block mb-2 text-sm font-bold text-gray-700">
//               Amount Paid
//             </label>
//             <input
//               type="number"
//               value={amountPaid}
//               onChange={(e) => setAmountPaid(Number(e.target.value))}
//               className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Reference No */}
//           <div>
//             <label className="block mb-2 text-sm font-bold text-gray-700">
//               Reference No
//             </label>
//             <input
//               type="text"
//               value={refNo}
//               onChange={(e) => setRefNo(e.target.value)}
//               className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>
//         </div>

//         {/* Right Column */}
//         <div className="space-y-4">
//           {/* Mobile */}
//           <div>
//             <label className="block mb-2 text-sm font-bold text-gray-700">
//               Mobile
//             </label>
//             <input
//               type="text"
//               value={orderDetails?.MobileNo || ''}
//               readOnly
//               className="w-full p-2 border rounded-lg bg-gray-100 text-gray-700"
//             />
//           </div>

//           {/* Balance */}
//           <div>
//             <label className="block mb-2 text-sm font-bold text-gray-700">
//               Balance
//             </label>
//             <input
//               type="text"
//               value={(orderDetails?.Balance || 0).toLocaleString('en-IN', {
//                 style: 'currency',
//                 currency: 'INR',
//               })}
//               readOnly
//               className="w-full p-2 border rounded-lg bg-gray-100 text-gray-700"
//             />
//           </div>

//           {/* Order Date */}
//           <div>
//             <label className="block mb-2 text-sm font-bold text-gray-700">
//               Delivery Date
//             </label>
//             <input
//               type="date"
//               value={orderDate}
//               onChange={(e) => setOrderDate(e.target.value)}
//               className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//             />
//           </div>

//           {/* Pay Mode */}
//           <div>
//             <label className="block mb-2 text-sm font-bold text-gray-700">
//               Pay Mode
//             </label>
//             <select
//               value={payMode}
//               onChange={(e) => setPayMode(e.target.value)}
//               disabled={!!orderDetails?.Paymode}
//               className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//             >
//               <option value="">Select</option>
//               {paymentModes.map((mode, index) => (
//                 <option key={index} value={mode.Mode}>
//                   {mode.Mode}
//                 </option>
//               ))}
//             </select>
//           </div>

//           {/* Sales Type */}
//           <div>
//             <label className="block mb-2 text-sm font-bold text-gray-700">
//               Sales Type
//             </label>

//             {/* Dropdown bound to SubTypeCode */}
//             <select
//               value={orderDetails.SubTypeCode || ""}
//               onChange={(e) => {
//                 const selected = orderDetails.Table3.find(
//                   (st) => st.TypeStr === e.target.value
//                 );
//                 setOrderDetails({
//                   ...orderDetails,
//                   SubTypeCode: selected?.TypeStr || "", // ✅ hidden field value
//                   SubTypeName: selected?.TypeName || "", // ✅ visible label
//                 });
//               }}
//               className="w-full p-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
//             >
//               <option value="">Select Sales Type</option>
//               {orderDetails.Table3?.map((st, idx) => (
//                 <option key={idx} value={st.TypeStr}>
//                   {st.TypeName}
//                 </option>
//               ))}
//             </select>

//             {/* Hidden input to hold TypeStr (SubTypeCode) */}
//             <input
//               type="hidden"
//               name="SubTypeCode"
//               value={orderDetails.SubTypeCode || ""}
//             />
//           </div>

//         </div>
//       </div>

//       {/* Buttons */}
//       <div className="flex justify-end gap-3 mt-6">
//         <Button
//           type="button"
//           onClick={handleUpdateOrder}
//           disabled={loading}
//           className="px-4 py-2"
//         >
//           {loading ? 'Updating...' : 'Update Sales Order Details'}
//         </Button>
//       </div>
//     </div>
//   );
// };

// export default SalesOrderHeader;


